import React, { useEffect } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Container,
  Box,
  CssBaseline,
  Card,
  CardContent,
  CardActions,
  Button,
  Typography,
} from "@mui/material";
import { SignInStatus } from "./states";
import { PREMIUM_PLAN, PREMIUM_PRICE_CNY_PER_MONTH } from "./billing";
import { INQUIRY_FORM_URL } from "./links";
import { pushDataLayerEvent } from "./analytics";

interface Plan {
  name: string;
  title: string;
  price: string;
  per?: string;
  priceNote?: string;
  free?: boolean;
  featured?: boolean;
  business?: boolean;
  description: string[];
}

// 縦積み（1 カラム）のときの並び順。DOM 上の順序でもある。
// 中国語圏はモバイルが大半なので、DOM の順序はモバイルの見た目に合わせている。
const plans: Plan[] = [
  {
    name: PREMIUM_PLAN,
    title: "Readable Pro",
    price: String(PREMIUM_PRICE_CNY_PER_MONTH),
    per: "月",
    priceNote: "年付买断型",
    featured: true,
    description: [
      `每月仅需 ${PREMIUM_PRICE_CNY_PER_MONTH} 元，读文档更轻松`,
      "7天无理由退款",
      "翻译数量不限",
      "每次可翻译 100 页，最大 50 MB",
      "翻译文件只需要大约 30 秒",
      "使用自主研发的深度学习模型",
    ],
  },
  {
    name: "free",
    title: "Readable Free",
    price: "0",
    free: true,
    description: [
      "每月可免费翻译 3 次",
      "一键翻译，无需手动操作",
      "分享微博可获得额外次数",
    ],
  },
  {
    name: "business",
    title: "Readable Business",
    price: "ASK",
    business: true,
    description: [
      "翻译数量不限",
      "每次可翻译 100 页，最大 50 MB",
      "翻译文件只需要大约 30 秒",
      "使用自主研发的深度学习模型",
    ],
  },
];

// 横並び（lg 以上）のときの左からの並び順。
// DOM の順序とは違うので CSS の order で並べ替える。
const COLUMN_ORDER: string[] = ["free", PREMIUM_PLAN, "business"];

// 並び順の定義から漏れたプランが黙って先頭に寄るのを防ぐ。
// plans と COLUMN_ORDER は同じプランの集合でなければならない。
const missingFromColumnOrder = plans
  .map((plan) => plan.name)
  .filter((name) => !COLUMN_ORDER.includes(name));
if (
  COLUMN_ORDER.length !== plans.length ||
  missingFromColumnOrder.length > 0
) {
  throw new Error(
    `COLUMN_ORDER must list every plan exactly once (missing: ${missingFromColumnOrder.join(", ")})`,
  );
}

interface Props {
  signInStatus: SignInStatus;
}

const Pricing = (props: Props) => {
  // GTM カスタムイベント: pricing ページ表示
  useEffect(() => {
    pushDataLayerEvent("viewpricing");
  }, []);

  return (
    <Container component="main" maxWidth="lg">
      <CssBaseline />
      <Box
        sx={{
          marginTop: 8,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          fontWeight="fontWeightBold"
          sx={{ mb: 4 }}
        >
          选择方案
        </Typography>
        {/*
          MUI の Grid container は負のマージンで隙間を作るため、
          幅の狭い画面では左右の余白が非対称になったり画面からはみ出したりする。
          CSS Grid の gap を使えばはみ出しは起きない。
          並び順は各カードの order で切り替える（COLUMN_ORDER を参照）。
        */}
        <Box
          sx={{
            width: "100%",
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "repeat(3, 1fr)" },
            gap: { xs: 3, lg: 5 },
            alignItems: "stretch",
          }}
        >
          {plans.map((plan) => (
            <Card
              key={plan.name}
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                order: { lg: COLUMN_ORDER.indexOf(plan.name) },
              }}
            >
              <Typography
                component="h2"
                variant="h4"
                align="center"
                sx={{ mt: 2, mb: { xs: 4, lg: 8 } }}
              >
                {plan.title}
              </Typography>

              <CardContent sx={{ flexGrow: 1 }}>
                {plan.featured && (
                  <Typography
                    component="p"
                    variant="h6"
                    color="info.main"
                    sx={{
                      position: "relative",
                      top: -20,
                      height: 0,
                      display: "flex",
                      justifyContent: "flex-end",
                    }}
                  >
                    推荐方案
                  </Typography>
                )}

                {/*
                  通貨記号と単位は価格のフォントサイズに対する相対値で置く。
                  こうしておけば画面幅で価格の文字サイズが変わっても崩れない。
                */}
                <Typography
                  component="p"
                  variant="h1"
                  sx={{
                    fontWeight: "medium",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "baseline",
                    mb: plan.priceNote ? 1 : { xs: 4, lg: 8 },
                  }}
                >
                  {!plan.business && (
                    <Box
                      component="span"
                      sx={{ fontSize: "0.35em", alignSelf: "flex-start" }}
                    >
                      ￥
                    </Box>
                  )}
                  {plan.price}
                  {plan.per && (
                    <Box
                      component="span"
                      sx={{ fontSize: "0.21em", color: "text.secondary" }}
                    >
                      /{plan.per}
                    </Box>
                  )}
                </Typography>

                {plan.priceNote && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    align="center"
                    sx={{ mb: { xs: 3, lg: 6 } }}
                  >
                    {plan.priceNote}
                  </Typography>
                )}

                {plan.description.map((desc) => (
                  <Typography variant="body1" color="text.secondary" key={desc}>
                    {desc}
                  </Typography>
                ))}
              </CardContent>

              <CardActions
                sx={{
                  mt: "auto",
                  display: "flex",
                  justifyContent: "center",
                  pb: 4,
                }}
              >
                {plan.free &&
                  (props.signInStatus === SignInStatus.PREMIUM ? (
                    <Button sx={{ width: 200, height: 40 }} disabled>
                      已成为专业会员
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      to="/translate"
                      sx={{ width: 200, height: 40 }}
                      component={RouterLink}
                    >
                      立即翻译
                    </Button>
                  ))}

                {plan.featured &&
                  (props.signInStatus === SignInStatus.PREMIUM ? (
                    <Button sx={{ width: 200, height: 40 }} disabled>
                      已注册
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      to={"/signup?plan=" + plan.name}
                      sx={{ width: 200, height: 40 }}
                      component={RouterLink}
                    >
                      注册专业会员（试用一周）
                    </Button>
                  ))}

                {plan.business && (
                  <Button
                    variant="contained"
                    href={INQUIRY_FORM_URL}
                    target="_blank"
                    rel="noreferrer"
                    sx={{ width: 200, height: 40 }}
                  >
                    联系咨询
                  </Button>
                )}
              </CardActions>
            </Card>
          ))}
        </Box>
        {props.signInStatus !== SignInStatus.PREMIUM && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "baseline",
            }}
          >
            <Button
              variant="text"
              sx={{ mt: 5, mb: 2 }}
              to="/signup?plan=ticket"
              component={RouterLink}
            >
              使用激活码
            </Button>
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default Pricing;
